/**
 * Login Page Component
 *
 * Handles user authentication and registration.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  authService,
  type LoginFailureReason,
  type UserRole,
  USER_ROLES,
} from "../services/auth";
import "@awesome.me/webawesome/dist/components/input/input.js";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import "@awesome.me/webawesome/dist/components/checkbox/checkbox.js";

type AuthMode = "login" | "register" | "forgot";

@customElement("login-page")
export class LoginPage extends LitElement {
  @state() private mode: AuthMode = "login";
  @state() private email = "";
  @state() private password = "";
  @state() private confirmPassword = "";
  @state() private displayName = "";
  @state() private isLoading = false;
  @state() private error = "";
  @state() private successMessage = "";
  @state() private staySignedIn = true;
  @state() private selectedRoles: Set<UserRole> = new Set([
    USER_ROLES.DIABETIC,
  ]);

  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      background: #eff7ff;
      position: relative;
      overflow: hidden;
    }

    .bg-artwork {
      position: absolute;
      inset: 0;
      pointer-events: none;
      z-index: 0;
    }

    .bg-artwork .wave {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }

    .bg-artwork .blob {
      position: absolute;
      border-radius: 50%;
    }

    .bg-artwork .blob-blue-1 {
      width: 143px;
      height: 143px;
      top: 35%;
      right: -2%;
      background: rgba(115, 171, 250, 0.75);
    }

    .bg-artwork .blob-purple-1 {
      width: 127px;
      height: 128px;
      bottom: 28%;
      left: -3%;
      background: rgba(189, 100, 252, 0.75);
    }

    .bg-artwork .blob-pink-1 {
      width: 242px;
      height: 242px;
      top: 20%;
      right: -6%;
      background: rgba(255, 0, 84, 0.5);
    }

    .bg-artwork .blob-blue-2 {
      width: 330px;
      height: 330px;
      bottom: -5%;
      left: -8%;
      background: rgba(115, 171, 250, 0.75);
    }

    .bg-artwork .blob-blue-3 {
      width: 110px;
      height: 105px;
      top: 18%;
      left: 25%;
      background: #73abfa;
      filter: drop-shadow(6px 1px 9.3px rgba(0, 0, 0, 0.12));
    }

    .bg-artwork .blob-pink-2 {
      width: 68px;
      height: 65px;
      top: 13%;
      left: 30%;
      background: rgba(255, 0, 84, 0.35);
      filter: drop-shadow(4px 4px 4px rgba(0, 0, 0, 0.12));
    }

    .bg-artwork .blob-blue-4 {
      width: 75px;
      height: 72px;
      bottom: 25%;
      right: 20%;
      background: rgba(115, 171, 250, 0.75);
      filter: drop-shadow(-4px 2px 4px rgba(0, 0, 0, 0.12));
    }

    .bg-artwork .blob-pink-3 {
      width: 120px;
      height: 115px;
      bottom: 20%;
      right: 17%;
      background: rgba(255, 0, 84, 0.5);
      filter: drop-shadow(-5px -5px 4px rgba(0, 0, 0, 0.12));
    }

    .container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      padding: 1.5rem;
      box-sizing: border-box;
      position: relative;
      z-index: 1;
    }

    .card {
      background: rgba(246, 246, 246, 0.55);
      backdrop-filter: blur(20px) saturate(180%);
      -webkit-backdrop-filter: blur(20px) saturate(180%);
      border: 1px solid rgba(255, 255, 255, 0.5);
      border-radius: 43px;
      box-shadow:
        0 7px 9.5px rgba(0, 0, 0, 0.28),
        inset 0 1px 0 rgba(255, 255, 255, 0.4);
      padding: 2.5rem 2rem;
      width: 100%;
      max-width: 376px;
      box-sizing: border-box;
    }

    .logo {
      text-align: center;
      margin-bottom: 1.5rem;
    }

    .logo h1 {
      margin: 0;
      font-size: 2.5rem;
      color: #bb64fb;
      font-weight: 700;
      letter-spacing: 0.02em;
    }

    .logo .subtitle {
      margin: 0.25rem 0 0 0;
      color: rgba(62, 117, 194, 0.5);
      font-size: 0.75rem;
      letter-spacing: 0.05em;
    }

    h2 {
      margin: 0 0 1.5rem 0;
      font-size: 1.25rem;
      text-align: center;
      color: #333;
    }

    .form-group {
      margin-bottom: 1rem;
    }

    wa-input {
      width: 100%;
    }

    wa-input::part(base) {
      border-radius: 0;
      border: none;
      border-bottom: 1px solid rgba(0, 0, 0, 0.5);
      background: transparent;
      box-shadow: none;
      padding-left: 0;
    }

    wa-input::part(base):focus-within {
      border-bottom-color: #bd64fc;
      box-shadow: none;
    }

    .checkbox-group {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin: 1rem 0;
      font-size: 0.875rem;
      color: #666;
    }

    .checkbox-group input {
      width: 1rem;
      height: 1rem;
    }

    .role-selection {
      margin: 1rem 0;
      padding: 1rem;
      background: rgba(255, 255, 255, 0.5);
      border-radius: 0.75rem;
    }

    .role-selection-label {
      font-weight: 500;
      font-size: 0.875rem;
      color: #333;
      margin-bottom: 0.75rem;
      display: block;
    }

    .role-options {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .role-option {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
    }

    .role-option wa-checkbox::part(label) {
      font-size: 0.875rem;
    }

    .role-description {
      font-size: 0.75rem;
      color: #6c757d;
      margin-left: 1.5rem;
    }

    wa-button {
      width: auto;
      display: flex;
      justify-content: center;
    }

    wa-button::part(base) {
      border-radius: 15px;
      background: #bd64fc;
      border-color: #bd64fc;
      padding: 0.5rem 2.5rem;
      font-size: 0.95rem;
      font-weight: 600;
    }

    wa-button::part(base):hover {
      background: #a84de6;
      border-color: #a84de6;
    }

    .error-alert {
      margin-bottom: 1rem;
    }

    .success-alert {
      margin-bottom: 1rem;
    }

    .links {
      margin-top: 1.5rem;
      text-align: center;
    }

    .links a {
      color: rgba(0, 0, 0, 0.5);
      text-decoration: none;
      font-size: 0.8rem;
      cursor: pointer;
    }

    .links a:hover {
      color: #bd64fc;
      text-decoration: underline;
    }

    .divider {
      margin: 0.75rem 0;
      color: #999;
      font-size: 0.8rem;
    }

    .forgot-link {
      text-align: right;
      margin: 0.5rem 0 1.25rem 0;
    }

    .forgot-link a {
      color: rgba(0, 0, 0, 0.5);
      text-decoration: none;
      font-size: 0.8rem;
      cursor: pointer;
    }

    .forgot-link a:hover {
      color: #bd64fc;
      text-decoration: underline;
    }

    .help-link {
      margin-top: 1.5rem;
      padding-top: 0.75rem;
      border-top: 1px solid rgba(0, 0, 0, 0.1);
      text-align: center;
    }

    .help-link a {
      color: rgba(0, 0, 0, 0.5);
      text-decoration: none;
      font-size: 0.8rem;
      cursor: pointer;
    }

    .help-link a:hover {
      color: #bd64fc;
      text-decoration: underline;
    }

    .offline-notice {
      background: #fef3cd;
      border: 1px solid #ffc107;
      border-radius: 0.75rem;
      padding: 0.75rem;
      margin-bottom: 1rem;
      font-size: 0.875rem;
      color: #856404;
      text-align: center;
    }

    .btn-row {
      display: flex;
      justify-content: center;
      margin-top: 0.5rem;
    }

    @media (max-width: 480px) {
      .container {
        padding: 1.25rem;
      }

      .card {
        border-radius: 28px;
        padding: 2rem 1.5rem;
      }

      .logo .subtitle {
        color: #3e75c2;
        font-size: 0.85rem;
        font-weight: 500;
      }

      .links a,
      .forgot-link a {
        color: #333;
        font-size: 0.9rem;
        font-weight: 600;
      }

      /* Reposition blobs to corners/edges so they're visible around the card */
      .bg-artwork .blob-blue-1 {
        width: 100px;
        height: 100px;
        top: 4%;
        right: -25px;
      }

      .bg-artwork .blob-purple-1 {
        width: 90px;
        height: 90px;
        top: 6%;
        left: -30px;
        bottom: auto;
      }

      .bg-artwork .blob-pink-1 {
        width: 140px;
        height: 140px;
        top: -40px;
        right: -50px;
      }

      .bg-artwork .blob-blue-2 {
        width: 200px;
        height: 200px;
        bottom: -60px;
        left: -50px;
      }

      .bg-artwork .blob-blue-3 {
        width: 75px;
        height: 75px;
        top: auto;
        bottom: 8%;
        left: auto;
        right: 8%;
      }

      .bg-artwork .blob-pink-2 {
        width: 50px;
        height: 50px;
        top: auto;
        bottom: 14%;
        left: auto;
        right: 18%;
      }

      .bg-artwork .blob-blue-4 {
        width: 60px;
        height: 60px;
        bottom: 4%;
        right: -20px;
      }

      .bg-artwork .blob-pink-3 {
        width: 90px;
        height: 90px;
        bottom: -20px;
        right: -30px;
      }
    }
  `;

  render() {
    const isOffline = !navigator.onLine;

    return html`
      <div class="bg-artwork">
        <!-- Decorative blobs -->
        <div class="blob blob-blue-1"></div>
        <div class="blob blob-purple-1"></div>
        <div class="blob blob-pink-1"></div>
        <div class="blob blob-blue-2"></div>
        <div class="blob blob-blue-3"></div>
        <div class="blob blob-pink-2"></div>
        <div class="blob blob-blue-4"></div>
        <div class="blob blob-pink-3"></div>

        <!-- Green wave (matches Figma viewBox 1920x1080) -->
        <svg
          class="wave"
          viewBox="0 0 1920 1080"
          preserveAspectRatio="xMidYMid slice"
        >
          <path
            d="M957 675.5C733 681.5 704 566 560.5 474C321.549 320.805 89 589 -103 727L-66 1121H2006V658C2006 488.5 1954.7 751.2 1807.5 710C1623.5 658.5 1523 489 1359.5 514.5C1196 540 1181 669.5 957 675.5Z"
            fill="rgba(125, 211, 182, 0.5)"
          />
        </svg>
      </div>

      <div class="container">
        <div class="card">
          <div class="logo">
            <h1>DAFNE LOG</h1>
            <p class="subtitle">Diabetes Management Simple</p>
          </div>

          ${isOffline
            ? html`
                <div class="offline-notice">
                  <wa-icon name="wifi-slash"></wa-icon>
                  You're offline. Login when connected.
                </div>
              `
            : ""}
          ${this.error
            ? html`
                <wa-callout variant="danger" open class="error-alert">
                  <wa-icon slot="icon" name="circle-exclamation"></wa-icon>
                  ${this.error}
                </wa-callout>
              `
            : ""}
          ${this.successMessage
            ? html`
                <wa-callout variant="success" open class="success-alert">
                  <wa-icon slot="icon" name="circle-check"></wa-icon>
                  ${this.successMessage}
                </wa-callout>
              `
            : ""}
          ${this.mode === "login"
            ? this.renderLogin()
            : this.mode === "register"
              ? this.renderRegister()
              : this.renderForgotPassword()}
        </div>
      </div>
    `;
  }

  private renderLogin() {
    return html`
      <h2>Sign In</h2>
      <form @submit=${this.handleLogin}>
        <div class="form-group">
          <wa-input
            type="email"
            name="email"
            autocomplete="username"
            label="email"
            placeholder=""
            required
            .value=${this.email}
            @input=${(e: Event) => {
              const input = e.target as any;
              this.email = input.value;
            }}
            ?disabled=${this.isLoading}
          ></wa-input>
        </div>
        <div class="form-group">
          <wa-input
            type="password"
            name="password"
            autocomplete="current-password"
            label="password"
            placeholder=""
            required
            .value=${this.password}
            @input=${(e: Event) => {
              const input = e.target as any;
              this.password = input.value;
            }}
            ?disabled=${this.isLoading}
          ></wa-input>
        </div>
        <div class="forgot-link">
          <a @click=${() => this.switchMode("forgot")}>Forgot password?</a>
        </div>
        <div class="btn-row">
          <wa-button
            type="submit"
            variant="brand"
            ?loading=${this.isLoading}
            ?disabled=${!navigator.onLine}
          >
            ${this.isLoading ? "Signing in..." : "Sign In"}
          </wa-button>
        </div>
      </form>
      <div class="links">
        <a @click=${() => this.switchMode("register")}>Create Account</a>
      </div>
    `;
  }

  private renderRegister() {
    return html`
      <h2>Create Account</h2>
      <form @submit=${this.handleRegister}>
        <div class="form-group">
          <wa-input
            type="text"
            name="name"
            autocomplete="name"
            label="Display Name"
            placeholder="Your name"
            required
            .value=${this.displayName}
            @input=${(e: Event) => {
              const input = e.target as any;
              this.displayName = input.value;
            }}
            ?disabled=${this.isLoading}
          ></wa-input>
        </div>
        <div class="form-group">
          <wa-input
            type="email"
            name="email"
            autocomplete="email"
            label="Email"
            placeholder="you@example.com"
            required
            .value=${this.email}
            @input=${(e: Event) => {
              const input = e.target as any;
              this.email = input.value;
            }}
            ?disabled=${this.isLoading}
          ></wa-input>
        </div>
        <div class="form-group">
          <wa-input
            type="password"
            name="password"
            autocomplete="new-password"
            label="Password"
            placeholder="Choose a strong password"
            required
            minlength="8"
            .value=${this.password}
            @input=${(e: Event) => {
              const input = e.target as any;
              this.password = input.value;
            }}
            ?disabled=${this.isLoading}
          ></wa-input>
        </div>
        <div class="form-group">
          <wa-input
            type="password"
            name="confirm-password"
            autocomplete="new-password"
            label="Confirm Password"
            placeholder="Re-enter your password"
            required
            .value=${this.confirmPassword}
            @input=${(e: Event) => {
              const input = e.target as any;
              this.confirmPassword = input.value;
            }}
            ?disabled=${this.isLoading}
          ></wa-input>
        </div>

        <div class="role-selection">
          <span class="role-selection-label"
            >I am a... (select all that apply)</span
          >
          <div class="role-options">
            <div class="role-option">
              <wa-checkbox
                ?checked=${this.selectedRoles.has(USER_ROLES.DIABETIC)}
                @change=${(e: Event) =>
                  this.toggleRole(
                    USER_ROLES.DIABETIC,
                    (e.target as HTMLInputElement).checked,
                  )}
                ?disabled=${this.isLoading}
              >
                Person with Diabetes
              </wa-checkbox>
            </div>
            <div class="role-description">
              I manage my own blood glucose and CGM data
            </div>

            <div class="role-option">
              <wa-checkbox
                ?checked=${this.selectedRoles.has(USER_ROLES.MONITOR)}
                @change=${(e: Event) =>
                  this.toggleRole(
                    USER_ROLES.MONITOR,
                    (e.target as HTMLInputElement).checked,
                  )}
                ?disabled=${this.isLoading}
              >
                Friend/Family Monitor
              </wa-checkbox>
            </div>
            <div class="role-description">
              I monitor a family member or friend's data
            </div>

            <div class="role-option">
              <wa-checkbox
                ?checked=${this.selectedRoles.has(USER_ROLES.CLINICIAN)}
                @change=${(e: Event) =>
                  this.toggleRole(
                    USER_ROLES.CLINICIAN,
                    (e.target as HTMLInputElement).checked,
                  )}
                ?disabled=${this.isLoading}
              >
                Healthcare Professional
              </wa-checkbox>
            </div>
            <div class="role-description">
              I'm a clinician monitoring patients
            </div>
          </div>
        </div>

        <wa-button
          type="submit"
          variant="brand"
          ?loading=${this.isLoading}
          ?disabled=${!navigator.onLine}
        >
          ${this.isLoading ? "Creating account..." : "Create Account"}
        </wa-button>
      </form>
      <div class="links">
        <a @click=${() => this.switchMode("login")}
          >Already have an account? Sign in</a
        >
      </div>
    `;
  }

  private renderForgotPassword() {
    return html`
      <h2>Reset Password</h2>
      <p
        style="text-align: center; color: #666; font-size: 0.875rem; margin-bottom: 1rem;"
      >
        Enter your email address and we'll send you a link to reset your
        password.
      </p>
      <form @submit=${this.handleForgotPassword}>
        <div class="form-group">
          <wa-input
            type="email"
            name="email"
            autocomplete="email"
            label="Email"
            placeholder="you@example.com"
            required
            .value=${this.email}
            @input=${(e: Event) => {
              const input = e.target as any;
              this.email = input.value;
            }}
            ?disabled=${this.isLoading}
          ></wa-input>
        </div>
        <wa-button
          type="submit"
          variant="brand"
          ?loading=${this.isLoading}
          ?disabled=${!navigator.onLine}
        >
          ${this.isLoading ? "Sending..." : "Send Reset Link"}
        </wa-button>
      </form>
      <div class="links">
        <a @click=${() => this.switchMode("login")}>Back to sign in</a>
      </div>
    `;
  }

  private switchMode(mode: AuthMode) {
    this.mode = mode;
    this.error = "";
    this.successMessage = "";
    // Don't clear password - might be useful when switching between login/register
  }

  private toggleRole(role: UserRole, checked: boolean) {
    const newRoles = new Set(this.selectedRoles);
    if (checked) {
      newRoles.add(role);
    } else {
      newRoles.delete(role);
    }
    this.selectedRoles = newRoles;
  }

  private async handleLogin(e: Event) {
    e.preventDefault();
    this.error = "";
    this.isLoading = true;

    // Get values directly from form controls (handles password manager autofill)
    const form = e.target as HTMLFormElement;
    const emailInput = form.querySelector('wa-input[name="email"]') as any;
    const passwordInput = form.querySelector(
      'wa-input[name="password"]',
    ) as any;
    const email = emailInput?.value || this.email;
    const password = passwordInput?.value || this.password;

    try {
      const result = await authService.login({
        email,
        password,
        staySignedIn: this.staySignedIn,
      });

      if (result.success) {
        // Dispatch event for app to handle
        this.dispatchEvent(
          new CustomEvent("auth-success", { bubbles: true, composed: true }),
        );
      } else {
        this.error = this.getErrorMessage(
          result.errorReason,
          result.errorMessage,
        );
      }
    } catch {
      this.error = "An unexpected error occurred. Please try again.";
    } finally {
      this.isLoading = false;
    }
  }

  private async handleRegister(e: Event) {
    e.preventDefault();
    this.error = "";

    // Get values directly from form controls (handles password manager autofill)
    const form = e.target as HTMLFormElement;
    const nameInput = form.querySelector('wa-input[name="name"]') as any;
    const emailInput = form.querySelector('wa-input[name="email"]') as any;
    const passwordInput = form.querySelector(
      'wa-input[name="password"]',
    ) as any;
    const confirmInput = form.querySelector(
      'wa-input[name="confirm-password"]',
    ) as any;
    const displayName = nameInput?.value || this.displayName;
    const email = emailInput?.value || this.email;
    const password = passwordInput?.value || this.password;
    const confirmPassword = confirmInput?.value || this.confirmPassword;

    // Validate passwords match
    if (password !== confirmPassword) {
      this.error = "Passwords do not match.";
      return;
    }

    // Validate password strength
    if (password.length < 8) {
      this.error = "Password must be at least 8 characters.";
      return;
    }

    // Validate at least one role selected
    if (this.selectedRoles.size === 0) {
      this.error = "Please select at least one role.";
      return;
    }

    this.isLoading = true;

    try {
      const result = await authService.register({
        email,
        password,
        displayName,
        roles: Array.from(this.selectedRoles),
      });

      if (result.success) {
        // Dispatch event for app to handle
        this.dispatchEvent(
          new CustomEvent("auth-success", { bubbles: true, composed: true }),
        );
      } else {
        this.error = this.getErrorMessage(
          result.errorReason,
          result.errorMessage,
        );
      }
    } catch {
      this.error = "An unexpected error occurred. Please try again.";
    } finally {
      this.isLoading = false;
    }
  }

  private async handleForgotPassword(e: Event) {
    e.preventDefault();
    this.error = "";
    this.successMessage = "";
    this.isLoading = true;

    // Get value directly from form control (handles password manager autofill)
    const form = e.target as HTMLFormElement;
    const emailInput = form.querySelector('wa-input[name="email"]') as any;
    const email = emailInput?.value || this.email;

    try {
      const result = await authService.forgotPassword(email);

      if (result.success) {
        this.successMessage = "Password reset email sent. Check your inbox.";
      } else {
        this.error = result.message || "Failed to send reset email.";
      }
    } catch {
      this.error = "An unexpected error occurred. Please try again.";
    } finally {
      this.isLoading = false;
    }
  }

  private getErrorMessage(
    reason?: LoginFailureReason,
    message?: string,
  ): string {
    switch (reason) {
      case "invalid_credentials":
        return "Invalid email or password.";
      case "account_locked":
        return "Your account has been locked. Please contact support.";
      case "too_many_attempts":
        return "Too many failed attempts. Please try again later.";
      case "email_not_verified":
        return "Please verify your email address before signing in.";
      case "two_factor_required":
        return "Two-factor authentication required.";
      case "password_not_good_enough":
        return "Password does not meet requirements.";
      default:
        return message || "An error occurred. Please try again.";
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "login-page": LoginPage;
  }
}
